import React, { useContext, useEffect, useState } from 'react';
import { Select, StatusIndicator } from '@amzn/awsui-components-core';
import { ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getSelectOption, extractUserAlias } from '../../api/UtilsApi';
import { DropdownStatusProps } from '@amzn/awsui-components-core/internal/components/dropdown-status';
import { ContentQueryContext } from '../content/table/ContentQueryContext';
import { WorkflowTableContext } from '../../contexts/WorkflowTableContext';
import { localLog } from '../../loggers/Logger';

interface Props {
  workflow: ContentWorkflow;
}

const TechWriterAssigneeSelect = ({ workflow }: Props) => {
  const { contentId, publicationWorkflowId, techWriterId } = workflow;
  const [isUpdatingTechWriter, setIsUpdatingTechWriter] = useState(false);
  const [allOptions, setAllOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const { fetchTechWritersState } = useContext(WorkflowTableContext);
  const { queryState, dispatchQueryState } = useContext(ContentQueryContext);

  // @ts-expect-error TS2339 - strict migration
  const { techWriters, isLoading, error } = fetchTechWritersState;

  const mplServiceClient = MplServiceClientFactory.create();

  useEffect(() => {
    const allTechWriters = techWriters;
    // @ts-expect-error TS7006 - strict migration
    const allTechWriterOptions = allTechWriters.map((techWriter) => {
      return {
        id: techWriter.userId,
        label: extractUserAlias(techWriter.userId),
        value: techWriter.userId,
      };
    });
    allTechWriterOptions.push({ id: 'Empty', label: '<Remove assignment>', value: '' });
    const selectedTechWriter = techWriterId
      ? getSelectOption(techWriterId, extractUserAlias(techWriterId))
      : null;
    setAllOptions(allTechWriterOptions);
    // @ts-expect-error TS2345 - strict migration
    setSelectedOption(selectedTechWriter);
  }, [contentId, techWriterId, techWriters]);

  if (fetchTechWritersState === undefined) {
    localLog.error(
      'Missing context value. TechWriterAssigneeSelect requires fetchTechWritersState.',
    );
    return <StatusIndicator type="error">Could not load tech writers</StatusIndicator>;
  }

  const updateAssignee = async (updatedOption: any) => {
    setSelectedOption(updatedOption);
    setIsUpdatingTechWriter(true);
    try {
      await mplServiceClient.assignTechWriter(
        contentId,
        publicationWorkflowId,
        updatedOption.value,
      );
    } finally {
      setIsUpdatingTechWriter(false);
    }

    // @ts-expect-error TS18048 - strict migration
    const tableItem = queryState.queryResult.find((wf) => wf.contentId === contentId);
    // @ts-expect-error TS18048 - strict migration
    tableItem.workflow.techWriterId = updatedOption.value;
    const modifiedQueryResult = [...queryState.queryResult];
    dispatchQueryState({ queryResult: modifiedQueryResult });
  };

  if (!publicationWorkflowId) {
    return null;
  }

  if (isUpdatingTechWriter) {
    return <StatusIndicator type="loading">Updating tech writer...</StatusIndicator>;
  }

  let statusType: DropdownStatusProps.StatusType = 'finished';
  if (error !== undefined) {
    statusType = 'error';
  }
  if (isLoading) {
    statusType = 'loading';
  }

  return (
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => updateAssignee(detail.selectedOption)}
      options={allOptions}
      selectedAriaLabel="Select"
      statusType={statusType}
      loadingText={isLoading ? 'loading tech writers' : undefined}
      errorText={error}
      expandToViewport
      ariaRequired
    />
  );
};

export default TechWriterAssigneeSelect;
